import {
  type FuelAnalysis,
  fuelAnalysis,
  type NextOperation,
  type ReportLocation,
  reportLocation,
  type VoyageReportKind,
} from '@felix/domain';
import type { Voyage, VoyageReport } from './types.js';
import {
  type VoyageProgress,
  voyageProgress,
  voyageReportsOf,
  voyageSummaryOf,
} from './voyage-record.js';

/**
 * Fuel & Consumption Analysis ve panonun sefer kartlarının saf yardımcıları (aşama V6; ADR 0090):
 * kapsamın raporları ve kartın özeti. Hesap domain'de (`fuelAnalysis`); burada yalnız seçim.
 */

export const FUEL_SCOPES = ['voyage', 'period'] as const;
export type FuelScope = (typeof FUEL_SCOPES)[number];

export type FuelScopeInput =
  | { kind: 'voyage'; voyage: Voyage }
  | { kind: 'period'; from: string; to: string };

const DAY_MS = 86_400_000;
const dayOf = (iso: string) => iso.slice(0, 10);
const byTime = (a: { reportedAt: string }, b: { reportedAt: string }) =>
  Date.parse(a.reportedAt) - Date.parse(b.reportedAt);

/**
 * Kapsamın raporları ve bitişi. Sefer: seferin raporları (bağlı ya da eski sefer kolonu;
 * `voyageReportsOf`), tamamlanan seferde bitiş seferin bitişi (`voyageSummaryOf` gibi). Dönem: UTC
 * günü `from`–`to` arasındaki raporlar (iki uç dahil), bitiş yok. İptal edilen rapor girmez.
 */
export function fuelScopeReports(
  reports: readonly VoyageReport[],
  scope: FuelScopeInput,
): { reports: VoyageReport[]; end: string | null } {
  const live = reports.filter((r) => r.status !== 'void');
  if (scope.kind === 'voyage') {
    return {
      reports: voyageReportsOf(live, scope.voyage),
      end: scope.voyage.status === 'completed' ? scope.voyage.end : null,
    };
  }
  const [from, to] = scope.from <= scope.to ? [scope.from, scope.to] : [scope.to, scope.from];
  return {
    reports: live.filter((r) => dayOf(r.reportedAt) >= from && dayOf(r.reportedAt) <= to),
    end: null,
  };
}

/** Kapsamın analizi. */
export function fuelAnalysisOf(
  reports: readonly VoyageReport[],
  scope: FuelScopeInput,
): FuelAnalysis {
  const own = fuelScopeReports(reports, scope);
  return fuelAnalysis(own.reports, { end: own.end });
}

/** Varsayılan dönem: son gönderilmiş raporun günü (yoksa bugün) ve ondan önceki 29 gün (UTC). */
export function defaultFuelPeriod(
  reports: readonly VoyageReport[],
  now: Date,
): { from: string; to: string } {
  const last = reports
    .filter((r) => r.status === 'submitted')
    .sort(byTime)
    .at(-1);
  const end = last ? Date.parse(last.reportedAt) : now.getTime();
  return {
    from: new Date(end - 29 * DAY_MS).toISOString().slice(0, 10),
    to: new Date(end).toISOString().slice(0, 10),
  };
}

/** Panonun sefer kartı (ADR 0090 §5). */
export interface VoyageCard {
  voyage: Voyage;
  progress: VoyageProgress;
  lastReport: { kind: VoyageReportKind; at: string; location: ReportLocation } | null;
  /** Son gönderilmiş raporun "Next Operation"ı; seçilmemişse `null`. */
  nextOperation: NextOperation | null;
  totalMt: number | null;
  dailyMt: number | null;
  robIssues: number;
}

/** Seferin kartı: ilerleme, son rapor ve konumu, sonraki işlem, tüketim özeti ve ROB farkları. */
export function voyageCardOf(voyage: Voyage, reports: readonly VoyageReport[]): VoyageCard {
  const own = voyageReportsOf(
    reports.filter((r) => r.status !== 'void'),
    voyage,
  );
  const summary = voyageSummaryOf(voyage, own);
  const analysis = fuelAnalysisOf(own, { kind: 'voyage', voyage });
  const last = own
    .filter((r) => r.status === 'submitted')
    .sort(byTime)
    .at(-1);
  const next = last?.data.nextOperation;
  return {
    voyage,
    progress: voyageProgress(voyage, summary),
    lastReport: last
      ? { kind: last.kind, at: last.reportedAt, location: reportLocation(last) }
      : null,
    nextOperation: typeof next === 'string' && next ? (next as NextOperation) : null,
    totalMt: analysis.totalMt,
    dailyMt: analysis.dailyMt,
    robIssues: analysis.issues,
  };
}
